// Client side of Phase 3: scoped, limited, live watchers over trips, containers, exceptions,
// alerts, devices, advances and intercompany invoices, plus the few reads the movement screens
// join to them (boxes at a branch, unpaid and held shipments, reserve allocations). All of these
// are written by the server; clients change them only through commands (04 §4.4, §4.5c, §4.6).
// Every query has a `where` on scope (branch or sector, or the parent document of a subcollection)
// and a `limit` (02 §2.3). The composite indexes each watcher needs are listed in its comment.
import {
  collection,
  doc,
  limit,
  orderBy,
  query,
  where,
  type DocumentData,
  type Firestore,
  type QueryConstraint,
} from 'firebase/firestore';
import { ulid } from 'ulid';
import { formatPrefixedId, type IdPrefix } from '@ecobox/domain';
import type {
  advanceDocSchema,
  alertDocSchema,
  carrierItemDocSchema,
  containerDocSchema,
  deviceDocSchema,
  documentDocSchema,
  exceptionDocSchema,
  intercompanyInvoiceDocSchema,
  intercompanyTransferDocSchema,
  cashCalendarDocSchema,
  reserveTotalsDocSchema,
  tripDocSchema,
} from '@ecobox/schema';
import type { BoxRow, ShipmentRow } from './counter';
import { toDoc, watchOne, watchRows, type Unsubscribe } from './docs';
import type { MoneyScope } from './money';

export type TripRow = (typeof tripDocSchema)['_output'];
export type CarrierItemRow = (typeof carrierItemDocSchema)['_output'];
export type DocumentRow = (typeof documentDocSchema)['_output'];
export type ContainerRow = (typeof containerDocSchema)['_output'];
export type ExceptionRow = (typeof exceptionDocSchema)['_output'];
export type AlertRow = (typeof alertDocSchema)['_output'];
export type DeviceRow = (typeof deviceDocSchema)['_output'];
export type AdvanceRow = (typeof advanceDocSchema)['_output'];
export type IntercompanyInvoiceRow = (typeof intercompanyInvoiceDocSchema)['_output'];
export type IntercompanyTransferRow = (typeof intercompanyTransferDocSchema)['_output'];
export type CashCalendarRow = (typeof cashCalendarDocSchema)['_output'];
export type ReserveTotalsRow = (typeof reserveTotalsDocSchema)['_output'];

/** A screen reads either one branch's documents, or a whole country's (a manager, accountant or director). */
export type MovementScope = MoneyScope;

export const TRIP_LIST_LIMIT = 50;
export const CONTAINER_LIST_LIMIT = 50;
/** The most boxes a container or trip item list loads (a 40 ft HC carries about 330). */
export const CARRIER_ITEMS_LIMIT = 600;
export const OPEN_EXCEPTION_STATUSES = ['open', 'investigating'] as const;
export const OPEN_ALERT_STATUSES = ['open', 'acknowledged'] as const;
/** Shipment statuses that are still moving: the control tower's "time since last state" list. */
export const MOVING_SHIPMENT_STATUSES = [
  'booked',
  'at_origin',
  'in_transit',
  'arrived',
  'out_for_delivery',
  'partially_delivered',
  'returning',
] as const;

const scopeFilter = (scope: MovementScope): QueryConstraint =>
  'branchId' in scope
    ? where('branchId', '==', scope.branchId)
    : where('sectorId', '==', scope.sectorId);

const row =
  <T>(idField: string) =>
  (id: string, data: DocumentData): T =>
    toDoc<T>(id, data, idField);

// ---- new ids -------------------------------------------------------------------

/** A new prefixed id such as `trp_01JA…`, made on the device so a command can name it offline. */
export function newMovementId(prefix: Extract<IdPrefix, 'trp' | 'ctn' | 'exc'>): string {
  return formatPrefixedId(prefix, ulid());
}

// ---- trips ---------------------------------------------------------------------

/**
 * Which trips: a branch's outgoing (`side: 'from'`) or incoming (`side: 'to'`), or a country's
 * (`sectorId`). A branch must say which end: a trip is read from either end, and the rules can
 * only prove the end the query filters on (`fromBranchId` or `toBranchId`).
 */
export type TripScope = { branchId: string; side: 'from' | 'to' } | { sectorId: string };

/**
 * Newest trips first. Indexes: `trips (fromBranchId asc, createdAt desc)`,
 * `trips (toBranchId asc, createdAt desc)` and `trips (sectorId asc, createdAt desc)`. With
 * `statuses` there is no ordering (no index); the screen sorts what comes back.
 */
export function watchTrips(
  db: Firestore,
  args: { scope: TripScope; statuses?: readonly string[]; max?: number },
  onData: (rows: TripRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const { scope } = args;
  const filter =
    'sectorId' in scope
      ? where('sectorId', '==', scope.sectorId)
      : where(scope.side === 'from' ? 'fromBranchId' : 'toBranchId', '==', scope.branchId);
  const constraints: QueryConstraint[] = [filter];
  if (args.statuses && args.statuses.length > 0) {
    constraints.push(where('status', 'in', [...args.statuses]));
  } else {
    constraints.push(orderBy('createdAt', 'desc'));
  }
  constraints.push(limit(args.max ?? TRIP_LIST_LIMIT));
  return watchRows(
    query(collection(db, 'trips'), ...constraints),
    row<TripRow>('id'),
    onData,
    onError,
  );
}

/** One trip, live. */
export function watchTrip(
  db: Firestore,
  tripId: string,
  onData: (row: TripRow | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchOne(doc(db, 'trips', tripId), row<TripRow>('id'), onData, onError);
}

// ---- the boxes on a trip or in a container ---------------------------------------

/**
 * The boxes on one trip or in one container. A subcollection is scoped by its parent, which the
 * caller already read. At most 600.
 */
export function watchCarrierItems(
  db: Firestore,
  args: { kind: 'trips' | 'containers'; id: string; max?: number },
  onData: (rows: CarrierItemRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, args.kind, args.id, 'items'),
    limit(Math.min(args.max ?? CARRIER_ITEMS_LIMIT, CARRIER_ITEMS_LIMIT)),
  );
  return watchRows(q, row<CarrierItemRow>('barcode'), onData, onError);
}

/** The most documents a container or shipment shows. */
export const DOCUMENTS_LIMIT = 50;

/**
 * The documents attached to one container or shipment (03 §3.4a), newest first. A subcollection is
 * scoped by its parent, whose rule decides who reads it. At most 50.
 */
export function watchDocuments(
  db: Firestore,
  args: { kind: 'container' | 'shipment'; id: string },
  onData: (rows: DocumentRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, args.kind === 'container' ? 'containers' : 'shipments', args.id, 'documents'),
    orderBy('addedAt', 'desc'),
    limit(DOCUMENTS_LIMIT),
  );
  return watchRows(q, row<DocumentRow>('id'), onData, onError);
}

// ---- containers ------------------------------------------------------------------

/**
 * Which containers: the ones a hub (or a country) ships out (`side: 'export'`, matched on the
 * export hub or the origin country) or receives (`side: 'import'`, the import hub or the
 * destination country). The side is required: a container is read from either hub, and the rules
 * can only prove the end the query filters on.
 */
export type ContainerScope =
  { branchId: string; side: 'export' | 'import' } | { sectorId: string; side: 'export' | 'import' };

function containerFilter(scope: ContainerScope): QueryConstraint {
  if ('sectorId' in scope) {
    return where(
      scope.side === 'export' ? 'originSectorId' : 'destinationSectorId',
      '==',
      scope.sectorId,
    );
  }
  return where(scope.side === 'export' ? 'exportHubId' : 'importHubId', '==', scope.branchId);
}

/**
 * Containers of a scope. With `statuses`, only those (no ordering, so no composite index; at most
 * 200). Without, the newest first. Indexes: `containers (<scope field> asc, createdAt desc)` for
 * `exportHubId`, `importHubId`, `originSectorId` and `destinationSectorId`.
 */
export function watchContainers(
  db: Firestore,
  args: { scope: ContainerScope; statuses?: readonly string[]; max?: number },
  onData: (rows: ContainerRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const constraints: QueryConstraint[] = [containerFilter(args.scope)];
  if (args.statuses && args.statuses.length > 0) {
    constraints.push(
      where('status', 'in', [...args.statuses]),
      limit(Math.min(args.max ?? 200, 200)),
    );
  } else {
    constraints.push(orderBy('createdAt', 'desc'), limit(args.max ?? CONTAINER_LIST_LIMIT));
  }
  return watchRows(
    query(collection(db, 'containers'), ...constraints),
    row<ContainerRow>('id'),
    onData,
    onError,
  );
}

/** One container, live. */
export function watchContainer(
  db: Firestore,
  containerId: string,
  onData: (row: ContainerRow | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchOne(doc(db, 'containers', containerId), row<ContainerRow>('id'), onData, onError);
}

// ---- exceptions ------------------------------------------------------------------

/**
 * The work queue: exceptions that are still open (or, with `statuses`, any states), by branch or
 * country. No ordering, so no composite index; the screen sorts by `stateSince`.
 */
export function watchExceptions(
  db: Firestore,
  args: { scope: MovementScope; statuses?: readonly string[]; max?: number },
  onData: (rows: ExceptionRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'exceptions'),
    scopeFilter(args.scope),
    where('status', 'in', [...(args.statuses ?? OPEN_EXCEPTION_STATUSES)]),
    limit(args.max ?? 200),
  );
  return watchRows(q, row<ExceptionRow>('id'), onData, onError);
}

/** The exceptions raised about one trip, container, box or shipment. Equality filters only. */
export function watchSubjectExceptions(
  db: Firestore,
  args: { branchId: string; kind: string; id: string },
  onData: (rows: ExceptionRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'exceptions'),
    where('branchId', '==', args.branchId),
    where('subject.kind', '==', args.kind),
    where('subject.id', '==', args.id),
    limit(50),
  );
  return watchRows(q, row<ExceptionRow>('id'), onData, onError);
}

/**
 * The exceptions a trip or container's scans raised (missing boxes, extra boxes, scans that did
 * not fit): their subject is the box, and `carrier` says which trip or container it was on.
 * Equality filters only.
 */
export function watchCarrierExceptions(
  db: Firestore,
  args: { branchId: string; carrierId: string },
  onData: (rows: ExceptionRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'exceptions'),
    where('branchId', '==', args.branchId),
    where('carrier.id', '==', args.carrierId),
    limit(200),
  );
  return watchRows(q, row<ExceptionRow>('id'), onData, onError);
}

// ---- alerts and devices ------------------------------------------------------------

/**
 * Alerts that are open or acknowledged, by branch or country. No ordering, so no composite
 * index; the screen sorts by severity and `raisedAt`.
 */
export function watchAlerts(
  db: Firestore,
  args: { scope: MovementScope; statuses?: readonly string[]; max?: number },
  onData: (rows: AlertRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'alerts'),
    scopeFilter(args.scope),
    where('status', 'in', [...(args.statuses ?? OPEN_ALERT_STATUSES)]),
    limit(args.max ?? 200),
  );
  return watchRows(q, row<AlertRow>('id'), onData, onError);
}

/** Devices that reported from a branch or country (heartbeat: pending commands, last sync). Equality filter only. */
export function watchDevices(
  db: Firestore,
  args: { scope: MovementScope; max?: number },
  onData: (rows: DeviceRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, 'devices'), scopeFilter(args.scope), limit(args.max ?? 100));
  return watchRows(q, row<DeviceRow>('deviceId'), onData, onError);
}

// ---- money between countries ---------------------------------------------------------

/**
 * A document between two countries is read by either one. A branch asks for the documents it
 * made (`branchId`, the hub that raised them); a country asks for both ends (`fromEntityId`
 * and `toEntityId`, two listeners merged), so the destination sees what is owed to it as well as
 * what it sent. Newest first, or, with `containerId`, those of one container.
 */
function watchTwoEnded<T extends { id: string; createdAt: string }>(
  db: Firestore,
  name: 'advances' | 'intercompanyInvoices' | 'intercompanyTransfers',
  args: { scope: MovementScope; containerId?: string; max?: number },
  onData: (rows: T[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const max = args.containerId ? 10 : (args.max ?? 50);
  const filters: QueryConstraint[][] =
    'branchId' in args.scope
      ? [[where('branchId', '==', args.scope.branchId)]]
      : [
          [where('fromEntityId', '==', args.scope.sectorId)],
          [where('toEntityId', '==', args.scope.sectorId)],
        ];
  const latest = filters.map(() => [] as T[]);
  const emit = (): void => {
    const merged = new Map(latest.flat().map((r) => [r.id, r]));
    onData(
      [...merged.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, max),
    );
  };
  const stops = filters.map((filter, i) =>
    watchRows(
      query(
        collection(db, name),
        ...filter,
        ...(args.containerId
          ? [where('containerId', '==', args.containerId)]
          : [orderBy('createdAt', 'desc')]),
        limit(max),
      ),
      row<T>('id'),
      (rows) => {
        latest[i] = rows;
        emit();
      },
      onError,
    ),
  );
  return () => stops.forEach((stop) => stop());
}

/**
 * Destination-cost advances, newest first, or those of one container. A branch sees the ones its
 * hub raised, a country the ones it sent and the ones sent to it. Indexes:
 * `advances (branchId | fromEntityId | toEntityId asc, createdAt desc)`.
 */
export function watchAdvances(
  db: Firestore,
  args: { scope: MovementScope; containerId?: string; max?: number },
  onData: (rows: AdvanceRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchTwoEnded<AdvanceRow>(db, 'advances', args, onData, onError);
}

/**
 * Intercompany invoices, newest first; a country sees the ones it raised and the ones it owes.
 * Indexes: `intercompanyInvoices (branchId | fromEntityId | toEntityId asc, createdAt desc)`.
 */
export function watchIntercompanyInvoices(
  db: Firestore,
  args: { scope: MovementScope; max?: number },
  onData: (rows: IntercompanyInvoiceRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchTwoEnded<IntercompanyInvoiceRow>(db, 'intercompanyInvoices', args, onData, onError);
}

/**
 * The cash calendar of one country (14 §14.8): written hourly by the server (`cashCalendar/{entityId}`),
 * so the screen reads one document and does no maths of its own. `null` until the first run.
 */
export function watchCashCalendar(
  db: Firestore,
  entityId: string,
  onData: (row: CashCalendarRow | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchOne(
    doc(db, 'cashCalendar', entityId),
    (_id, data) => toDoc<CashCalendarRow>(entityId, data),
    onData,
    onError,
  );
}

/**
 * Reserves a country holds, one document per currency (`reserveTotals/{entityId}__{currency}`,
 * kept by the payment commands, 14 §14.6). Held = allocated − released. Equality filter only.
 */
export function watchReserveTotals(
  db: Firestore,
  entityId: string,
  onData: (rows: ReserveTotalsRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, 'reserveTotals'), where('entityId', '==', entityId), limit(20));
  return watchRows(q, (id, data) => toDoc<ReserveTotalsRow>(id, data), onData, onError);
}

// ---- boxes and shipments the movement screens join ---------------------------------------

export const BOXES_HERE_LIMIT = 500;

/**
 * Boxes physically at a branch (`at_branch`), at most 500. A box keeps the `branchId` of the branch
 * that collected it for ever (that is what scope follows); where it is now is `currentBranchId`,
 * so a hub also sees the boxes that arrived from a satellite or a container. Equality filters only.
 */
export function watchBoxesHere(
  db: Firestore,
  args: { branchId: string; max?: number },
  onData: (rows: BoxRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'boxes'),
    where('currentBranchId', '==', args.branchId),
    where('status', '==', 'at_branch'),
    limit(Math.min(args.max ?? BOXES_HERE_LIMIT, BOXES_HERE_LIMIT)),
  );
  return watchRows(q, row<BoxRow>('barcode'), onData, onError);
}

/**
 * Shipments that are unpaid or part paid, so their boxes cannot be loaded (D-16). At most 500.
 * Equality filter plus an `in` filter: no composite index.
 */
export function watchUnpaidShipments(
  db: Firestore,
  args: { scope: MovementScope; max?: number },
  onData: (rows: ShipmentRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'shipments'),
    scopeFilter(args.scope),
    where('money.status', 'in', ['unpaid', 'partial']),
    limit(args.max ?? 500),
  );
  return watchRows(q, row<ShipmentRow>('trackingNo'), onData, onError);
}

/**
 * Shipments still moving, the least recently changed first: the control tower's "time since last
 * state". Index: `shipments (branchId asc, status asc, updatedAt asc)` and the same with `sectorId`.
 */
export function watchMovingShipments(
  db: Firestore,
  args: { scope: MovementScope; max?: number },
  onData: (rows: ShipmentRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'shipments'),
    scopeFilter(args.scope),
    where('status', 'in', [...MOVING_SHIPMENT_STATUSES]),
    orderBy('updatedAt', 'asc'),
    limit(args.max ?? 200),
  );
  return watchRows(q, row<ShipmentRow>('trackingNo'), onData, onError);
}

/** Shipments with an active hold. Equality filters only. */
export function watchHeldShipments(
  db: Firestore,
  args: { scope: MovementScope; max?: number },
  onData: (rows: ShipmentRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'shipments'),
    scopeFilter(args.scope),
    where('flags.onHold', '==', true),
    limit(args.max ?? 100),
  );
  return watchRows(q, row<ShipmentRow>('trackingNo'), onData, onError);
}

/**
 * Transfers between two countries that are not advances (14 §14.9), newest first; a country sees
 * the ones it sent and the ones it received. Indexes:
 * `intercompanyTransfers (branchId | fromEntityId | toEntityId asc, createdAt desc)`.
 */
export function watchIntercompanyTransfers(
  db: Firestore,
  args: { scope: MovementScope; max?: number },
  onData: (rows: IntercompanyTransferRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchTwoEnded<IntercompanyTransferRow>(db, 'intercompanyTransfers', args, onData, onError);
}
